import { useMemo } from 'react'
import {
  fcyLapEstimate,
  findCar,
  formatClock,
  isInPits,
  lastCrossingOf,
  measuredFcyLapMs,
  pitWindowAdvice,
  pitWindowPace,
  readClock,
  resolveWindows,
  sessionTrackStatus,
  stoppedInWindow,
  windowStateAt,
} from '@racedash/shared'
import { Tile } from '../Tile'
import type { WidgetProps } from './props'

/**
 * Is the pit window open, how long for, and how many more laps can we do.
 *
 * Green when open, red when closed, and a time as the headline in both:
 * closes in, or opens in. Under it the laps, from the last line crossing at
 * the car's fastest pace, so "cannot make it" and "stop next time round" are
 * certainties. The one pessimistic case is the yellow, which outranks the
 * lap count because it is the thing that turns a comfortable window into a
 * missed one without warning. See pitWindowAdvice for the rules.
 *
 * Only about windows. Stint limits and driver budgets have their own tile.
 */
export default function PitWindow({ car, data, now, context }: WidgetProps<'pitWindow'>) {
  const regime = context.plan?.regime
  const at = data.asAt(now)

  const raw = data.session?.clock
  const clock = readClock(
    raw?.at
      ? { at: raw.at.toDate(), elapsedMs: raw.elapsedMs, remainingMs: raw.remainingMs, running: raw.running }
      : null,
    at,
  )
  const track = sessionTrackStatus(data.session).status

  const sessionStart = data.session?.clockStartAt?.toDate() ?? context.sessionStart ?? null
  const windows = useMemo(
    () =>
      regime
        ? resolveWindows(regime, { startedAt: sessionStart, endsAt: context.sessionEnd ?? null })
        : [],
    [regime, sessionStart, context.sessionEnd],
  )

  const advice = useMemo(() => {
    if (!regime) return null
    const mine = car ? data.laps.filter((l) => l.car === car) : []
    const pace = pitWindowPace(mine, data.carSummary?.bestLapMs)
    const open = windowStateAt(windows, at).open
    const stops = car ? data.pitStops.filter((s) => s.car === car) : []
    return pitWindowAdvice({
      windows,
      now: at,
      lastCrossing: lastCrossingOf(mine, car),
      paceMs: pace,
      yellowLapMs: fcyLapEstimate({
        measuredMs: measuredFcyLapMs(data.laps),
        trackLengthMetres: context.trackLengthMetres ?? null,
        typicalLapMs: pace,
      }),
      underYellow: track === 'fcy' || track === 'safetyCar',
      raceStopped: track === 'red' || (clock ? !clock.running : false),
      stoppedAt: stoppedInWindow(open, at, stops, mine, isInPits(findCar(data.standings, car))),
    })
  }, [regime, windows, data, car, at, context, track, clock])

  // Every window closed before this session even started: not a race that has
  // used them up, but a plan dated for another day. Portimao's sat a week early
  // and this tile said "No more windows" all morning.
  const last = windows.at(-1)
  const wrongDay = last != null && sessionStart != null && last.closes.getTime() <= sessionStart.getTime()

  if (!regime || !advice || advice.phase === 'noWindows') {
    return (
      <Tile label="Pit window">
        <div className="pitwindow-blank">{regime ? 'No pit windows in the plan' : 'No race plan'}</div>
      </Tile>
    )
  }
  if (!car) {
    return (
      <Tile label="Pit window">
        <div className="pitwindow-blank">No car</div>
      </Tile>
    )
  }

  const { phase, urgency, window } = advice
  const tone =
    phase === 'finished'
      ? 'finished'
      : phase === 'closed'
        ? 'red'
        : urgency === 'cannotMake'
          ? 'critical'
          : 'green'

  const headline =
    phase === 'finished'
      ? wrongDay
        ? `Windows are for ${last.opens.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}`
        : 'No more windows'
      : urgency === 'cannotMake'
        ? 'Cannot make the window'
        : `${phase === 'open' ? 'closes' : 'opens'} in ${mmss(advice.remainingMs ?? 0)}`

  const laps =
    phase === 'finished'
      ? null
      : phase === 'open'
        ? urgency === 'cannotMake'
          ? `next pass ${formatClock(advice.nextPass as Date, { seconds: true })}, closes ${formatClock(window!.closes, { seconds: true })}`
          : advice.detail.replace(/\.$/, '')
        : advice.detail.replace(/\.$/, '')

  const warn = urgency === 'nextTimeRound' || urgency === 'yellowRisk'

  return (
    <Tile label="Pit window" tone={tone}>
      <div className="pitwindow">
        <div className="pitwindow-state">{phase === 'open' ? 'OPEN' : phase === 'closed' ? 'CLOSED' : ''}</div>
        <div className="pitwindow-time">{headline}</div>
        {laps && <div className={warn ? 'pitwindow-laps pitwindow-laps--warn' : 'pitwindow-laps'}>{laps}</div>}
        {window && (
          <div className="pitwindow-foot">
            window {window.index} of {advice.total} · {formatClock(window.opens)} to {formatClock(window.closes)}
          </div>
        )}
      </div>
    </Tile>
  )
}

/** m:ss, or h:mm:ss past an hour. Never negative. */
function mmss(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`
}
